import { defineRecipe } from "@pandacss/dev";
import buttonBaseStyles from "./utils/buttonBaseStyles";

export const aromaButton = defineRecipe({
  className: "aroma-button",
  jsx: ["Button"],
  base: buttonBaseStyles,

  variants: {
    size: {
      small: {
        height: "m",
        borderRadius: "s",
      },
      medium: {
        height: "xl",
      },
      large: {
        height: "2xl",
      },
    },
    iconOnly: {
      true: {
        paddingX: "10px",
      },
    },
    fullWidth: {
      true: {
        width: "100%",
      },
    },
    loading: {
      true: {
        bgRepeat: "no-repeat",
        bgSize: "200%",
        animation: "2500ms linear infinite aromaButtonLoading",
        _disabled: {
          bgImage:
            "linear-gradient({colors.background.neutral.disable.bold}, {colors.background.neutral.disable.bold})",
        },
      },
    },
    hierarchy: {
      "primary-bold": {
        bgColor: "background.brand.bold",
        color: "content.neutral.reverse.bold",
        _withinWhiteLabelTheme: {
          color: "content.neutral.bold",
        },
      },
      "primary-subtle": {
        borderColor: {
          base: "border.brand-bold",
        },
        color: "content.brand.bold",
        _withinWhiteLabelTheme: {
          borderColor: "border.brand.brand-brand",
          color: "content.brand.subtle",
        },
      },
      "primary-subtler": {
        color: "content.brand.bold",
        _withinWhiteLabelTheme: {
          color: "content.brand.subtle",
        },
      },
      "secondary-bold": {
        bgColor: "background.neutral.base.subtle",
        color: "content.neutral.base.bold",
      },
      "secondary-subtle": {
        borderColor: {
          base: "border.neutral.bold",
          _focusVisible: "border.brand-bold",
        },
        color: "content.neutral.base.subtle",
        _withinWhiteLabelTheme: {
          _focusVisible: {
            borderColor: "border.brand.brand-brand",
          },
        },
      },
      "secondary-subtler": {
        color: "content.neutral.base.subtle",
      },
      "destructive-bold": {
        color: "content.neutral.reverse.bold",
        bgColor: "background.functional.error.bold",
        _focusVisible: {
          borderStyle: "none",
          shadow: "effect.focus-ring.error",
        },
      },
      "destructive-subtle": {
        color: "content.functional.error",
        borderColor: {
          base: "border.functional.error",
        },
        _focusVisible: {
          shadow: "effect.focus-ring.error",
        },
      },
      "destructive-subtler": {
        color: "content.functional.error",
        _focusVisible: {
          borderColor: "border.functional.error",
          shadow: "effect.focus-ring.error",
        },
      },
      "brand-reversed-bold": {
        color: "content.brand.reverse-subtle",
        bgColor: "background.brand.complementary-reversed",
        _withinWhiteLabelTheme: {
          bgColor: "background.neutral-on-brand.base.bold",
          color: "content.brand.bold",
          _focusVisible: {
            shadow: "effect.focus-ring.neutral",
            borderColor: "border.neutral-on-brand.bold",
          },
        },
      },
      "brand-reversed-subtle": {
        borderColor: "border.brand-complementary-reversed",
        color: "content.brand.reverse-bold",
        _withinWhiteLabelTheme: {
          borderColor: "border.neutral-on-brand.bold",
          color: "content.neutral.subtle",
          _focusVisible: {
            shadow: "effect.focus-ring.neutral",
            borderColor: "border.neutral-on-brand.bold",
          },
        },
        _focusVisible: {
          borderColor: "border.brand-bold",
        },
      },
      "brand-reversed-subtler": {
        color: "content.brand.reverse-bold",
        _withinWhiteLabelTheme: {
          color: "content.neutral.subtle",
          _focusVisible: {
            shadow: "effect.focus-ring.neutral",
          },
        },
      },
      "reversed-bold": {
        bgColor: "background.neutral.base.subtlest",
        color: "content.neutral.base.bold",
        _withinWhiteLabelTheme: {
          _focusVisible: {
            shadow: "effect.focus-ring.neutral",
            borderColor: "transparent",
          },
        },
      },
      "reversed-subtle": {
        borderColor: "border.neutral.subtlest",
        color: "content.neutral.reverse.subtle",
        _withinWhiteLabelTheme: {
          _focusVisible: {
            shadow: "effect.focus-ring.neutral",
          },
        },
      },
      "reversed-subtler": {
        color: "content.neutral.reverse.subtle",
        _withinWhiteLabelTheme: {
          _focusVisible: {
            shadow: "effect.focus-ring.neutral!",
            borderColor: "transparent",
          },
        },
      },
      "ai-bolder": {
        bg: "background.ai.bold",
        color: "white",
        _notDisabled: {
          _hover: {
            bg: "background.ai.bold-hover",
          },
          _active: {
            bg: "background.ai.bold-active",
          },
        },
        _withinWhiteLabelTheme: {
          bg: "background.ai.bold",
          color: "white",
          _focusVisible: {
            shadow: "effect.focus-ring.ai",
            borderColor: "border.neutral.subtlest",
          },
        },
        _focusVisible: {
          borderWidth: "m",
          borderColor: "border.neutral.subtlest",
          borderStyle: "solid",
          shadow: "effect.focus-ring.ai",
        },
        _disabled: {
          bg: "background.neutral.disable.subtle",
          color: "content.neutral.base.disable",
        },
      },
      "ai-bold": {
        bg: "background.ai.subtle",
        color: "background.decorative.purple.hover-bold",
        _aromaticonChildren: {
          bg: "background.ai.bold-hover",
          color: "transparent",
          backgroundClip: "text",
        },
        "& span": {
          bg: "background.ai.bold-hover",
          color: "transparent",
          backgroundClip: "text",
        },

        _notDisabled: {
          _hover: {
            bg: "background.ai.subtle-hover",
            color: "background.decorative.purple.base-bolder",
            _aromaticonChildren: {
              bg: "background.ai.bold-active",
              color: "transparent",
              backgroundClip: "text",
            },
            "& span": {
              bg: "background.ai.bold-active",
              color: "transparent",
              backgroundClip: "text",
            },
          },
          _active: {
            bg: "background.ai.subtle-active",
          },
        },
        _withinWhiteLabelTheme: {
          bg: "background.ai.subtle",
          color: "background.decorative.purple.hover-bold",
          _aromaticonChildren: {
            bg: "background.ai.bold-hover",
            color: "transparent",
            backgroundClip: "text",
          },
          "& span": {
            bg: "background.ai.bold-hover",
            color: "transparent",
            backgroundClip: "text",
          },
          _focusVisible: {
            shadow: "effect.focus-ring.ai",
            borderColor: "border.neutral.subtlest",
          },
        },
        _focusVisible: {
          borderWidth: "m",
          borderColor: "border.neutral.subtlest",
          borderStyle: "solid",
          shadow: "effect.focus-ring.ai",
        },
        _disabled: {
          bg: "background.neutral.disable.subtle",
          color: "content.neutral.base.disable",
          _aromaticonChildren: {
            bg: "none",
            color: "content.neutral.base.disable",
          },
          "& span": {
            bg: "none",
            color: "content.neutral.base.disable",
          },
        },
      },
      "ai-subtle": {
        position: "relative",
        bg: "border.neutral.subtlest",
        color: "background.decorative.purple.base-bold",
        borderRadius: "m",
        borderImageSlice: 1,
        _aromaticonChildren: {
          bg: "background.ai.bold-hover",
          color: "transparent",
          backgroundClip: "text",
        },
        "& span": {
          bg: "background.ai.bold-hover",
          color: "transparent",
          backgroundClip: "text",
        },
        _before: {
          content: '""',
          position: "absolute",
          inset: "-1px",
          padding: "1px",
          borderRadius: "m",
          background: "{colors.border.ai-hover}",
          WebkitMask:
            "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
          WebkitMaskComposite: "xor",
          maskComposite: "exclude",
          pointerEvents: "none",
        },
        _notDisabled: {
          _hover: {
            bg: "background.ai.subtler-hover",
            color: "background.decorative.purple.base-bolder",
            _aromaticonChildren: {
              bg: "background.ai.bold-active",
              color: "transparent",
              backgroundClip: "text",
            },
            "& span": {
              bg: "background.ai.bold-active",
              color: "transparent",
              backgroundClip: "text",
            },
          },
          _active: {
            bg: "background.ai.subtler-active",
          },
        },
        _withinWhiteLabelTheme: {
          bg: "border.neutral.subtlest",
          color: "background.decorative.purple.hover-bold",
          borderRadius: "s",
          borderImageSlice: 1,
          _aromaticonChildren: {
            bg: "background.ai.bold-hover",
            color: "transparent",
            backgroundClip: "text",
          },
          "& span": {
            bg: "background.ai.bold-hover",
            color: "transparent",
            backgroundClip: "text",
          },
          _before: {
            content: '""',
            position: "absolute",
            inset: "-1px",
            padding: "1px",
            borderRadius: "s",
            background: "{colors.border.ai-hover}",
            WebkitMask:
              "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
            WebkitMaskComposite: "xor",
            maskComposite: "exclude",
            pointerEvents: "none",
          },
          _focusVisible: {
            border: "none",
            shadow: "effect.focus-ring.ai",
          },
        },
        _focusVisible: {
          border: "none",
          shadow: "effect.focus-ring.ai",
        },
        _disabled: {
          bg: "background.neutral.disable.subtle",
          color: "content.neutral.base.disable",
          _aromaticonChildren: {
            bg: "none",
            color: "content.neutral.base.disable",
          },
          "& span": {
            bg: "none",
            color: "content.neutral.base.disable",
          },
          _before: {
            display: "none",
          },
        },
      },
      "ai-subtler": {
        bg: "border.neutral.subtlest",
        color: "background.decorative.purple.hover-bold",
        _aromaticonChildren: {
          bg: "background.ai.bold-hover",
          color: "transparent",
          backgroundClip: "text",
        },
        "& span": {
          bg: "background.ai.bold-hover",
          color: "transparent",
          backgroundClip: "text",
        },
        _notDisabled: {
          _hover: {
            bg: "background.ai.subtler-hover",
            color: "background.decorative.purple.base-bolder",
            _aromaticonChildren: {
              bg: "background.ai.bold-active",
              color: "transparent",
              backgroundClip: "text",
            },
            "& span": {
              bg: "background.ai.bold-active",
              color: "transparent",
              backgroundClip: "text",
            },
          },
          _active: {
            bg: "background.ai.subtler-active",
          },
        },
        _withinWhiteLabelTheme: {
          bg: "border.neutral.subtlest",
          color: "background.decorative.purple.hover-bold",
          _aromaticonChildren: {
            bg: "background.ai.bold-hover",
            color: "transparent",
            backgroundClip: "text",
          },
          "& span": {
            bg: "background.ai.bold-hover",
            color: "transparent",
            backgroundClip: "text",
          },
          _focusVisible: {
            border: "none",
            shadow: "effect.focus-ring.ai",
          },
        },
        _focusVisible: {
          border: "none",
          shadow: "effect.focus-ring.ai",
        },
        _disabled: {
          bg: "background.neutral.disable.subtle",
          color: "content.neutral.base.disable",
          _aromaticonChildren: {
            bg: "none",
            color: "content.neutral.base.disable",
          },
          "& span": {
            bg: "none",
            color: "content.neutral.base.disable",
          },
        },
      },
    },
    active: {
      true: {},
    },
    floating: {
      true: {
        shadow: "effect.shadow.bolder-bottom",
      },
      false: {},
    },
  },
  defaultVariants: {
    size: "medium",
    iconOnly: false,
    hierarchy: "primary-bold",
    loading: false,
    active: false,
  },
  compoundVariants: [
    { iconOnly: true, size: "small", css: { width: "m" } },
    { iconOnly: true, size: "medium", css: { width: "xl" } },
    { iconOnly: true, size: "large", css: { width: "2xl" } },
    // not loading
    {
      loading: false,
      hierarchy: "primary-bold",
      css: {
        _disabled: {
          bgColor: "background.neutral.disable.subtle",
          borderColor: "transparent",
          color: {
            base: "content.neutral.base.disable",
            _withinWhiteLabelTheme: "content.neutral.base.disable",
          },
        },
        _notDisabled: {
          bgColor: {
            base: "background.brand.bold",
            _lowContrast: "background.brand.low-contrast",
          },
          color: {
            base: "content.neutral.reverse.bold",
            _lowContrast: "content.brand.bold",
          },
          _hover: {
            bgColor: {
              base: "background.brand.bold-hover",
              _lowContrast: "background.neutral-on-brand.base.bold-hover",
            },
          },
          _active: {
            bgColor: "background.brand.bold-active",
          },
          _expanded: {
            bgColor: "background.brand.bold-active",
          },
        },
      },
    },
    {
      loading: false,
      hierarchy: "primary-subtle",
      css: {
        _disabled: {
          color: {
            base: "content.neutral.base.disable",
            _withinWhiteLabelTheme: "content.neutral.base.disable",
          },
          borderColor: "transparent",
          bgColor: "background.neutral.disable.subtle",
        },
        _notDisabled: {
          _hover: {
            bgColor: "background.brand.subtler-hover",
            color: "content.brand.bold-hover",
          },
          _active: {
            bgColor: "background.brand.subtler-active",
          },
          _expanded: {
            bgColor: "background.neutral.base.subtlest-active",
          },
        },
      },
    },
    {
      loading: false,
      hierarchy: "primary-subtler",
      css: {
        _disabled: {
          color: {
            base: "content.neutral.base.disable",
            _withinWhiteLabelTheme: "content.neutral.base.disable",
          },
          borderColor: "transparent",
          bgColor: "background.neutral.disable.subtle",
        },
        _notDisabled: {
          _hover: {
            bgColor: "background.brand.subtler-hover",
            _withinWhiteLabelTheme: {
              borderColor: "border.brand.brand-brand",
            },
            color: "content.brand.bold-hover",
          },
          _active: {
            bgColor: "background.brand.subtler-active",
          },
          _expanded: {
            bgColor: "background.neutral.base.subtlest-active",
          },
        },
      },
    },
    {
      loading: false,
      hierarchy: "secondary-bold",
      css: {
        _disabled: {
          bgColor: "background.neutral.disable.subtle",
          borderColor: "transparent",
          color: "content.neutral.base.disable",
        },
        _notDisabled: {
          _hover: {
            bgColor: "background.neutral.base.subtle-hover",
          },
          _active: {
            bgColor: "background.neutral.base.subtle-active",
          },
          _expanded: {
            bgColor: "background.neutral.base.subtle-active",
          },
        },
      },
    },
    {
      loading: false,
      hierarchy: "secondary-subtle",
      active: false,
      css: {
        _disabled: {
          bgColor: "background.neutral.disable.subtle",
          borderColor: "transparent",
          color: "content.neutral.base.disable",
        },
        _notDisabled: {
          _hover: {
            bgColor: "background.neutral.base.subtlest-hover",
            borderColor: "border.neutral.bold-hover",
            color: "content.neutral.base.bold-hover",
          },
          _active: {
            bgColor: "background.neutral.base.subtlest-active",
            borderColor: "border.neutral.bold-active",
          },
          _expanded: {
            bgColor: "background.neutral.base.subtlest-active",
            borderColor: "border.neutral.bold-active",
          },
        },
      },
    },
    {
      loading: false,
      hierarchy: "secondary-subtle",
      active: true,
      css: {
        _disabled: {
          bgColor: "background.neutral.disable.subtle",
          borderColor: "transparent",
          color: "content.neutral.base.disable",
        },
        _notDisabled: {
          borderColor: "border.brand-bold",
          color: "content.neutral.base.bold",
          bgColor: {
            base: "background.brand.subtle",
            _hover: "background.brand.subtle-hover",
            _active: "background.brand.subtle-active",
            _focusWithin: "background.brand.subtle",
          },
        },
      },
    },
    {
      loading: false,
      hierarchy: "secondary-subtler",
      css: {
        _disabled: {
          bgColor: "none",
          borderColor: "transparent",
          color: "content.neutral.base.disable",
        },
        _notDisabled: {
          color: {
            base: "content.neutral.base.subtle",
            _hover: "content.neutral.base.bold-hover",
          },
          bgColor: {
            _hover: "background.neutral.base.subtlest-hover",
            _active: "background.neutral.base.subtlest-active",
            _expanded: "background.neutral.base.subtlest-active",
          },
        },
      },
    },
    {
      loading: false,
      hierarchy: "destructive-bold",
      css: {
        _disabled: {
          bgColor: "background.neutral.disable.subtle",
          borderColor: "transparent",
          color: "content.neutral.base.disable",
        },
        _notDisabled: {
          color: "content.neutral.reverse.bold",
          bgColor: {
            base: "background.functional.error.bold",
            _hover: "background.functional.error.bold-hover",
            _active: "background.functional.error.bold-active",
            _expanded: "background.functional.error.bold-active",
          },
        },
      },
    },
    {
      loading: false,
      hierarchy: "destructive-subtle",
      css: {
        _disabled: {
          bgColor: "background.neutral.disable.subtle",
          borderColor: "transparent",
          color: "content.neutral.base.disable",
        },
        _notDisabled: {
          _hover: {
            bgColor: "background.functional.error.subtle-hover",
          },
          _active: {
            bgColor: "background.functional.error.subtle-active",
          },
          _expanded: {
            bgColor: "background.functional.error.subtle-active",
          },
        },
      },
    },
    {
      loading: false,
      hierarchy: "destructive-subtler",
      css: {
        _disabled: {
          bgColor: "background.neutral.disable.subtle",
          borderColor: "transparent",
          color: "content.neutral.base.disable",
        },
        _notDisabled: {
          _hover: {
            bgColor: "background.functional.error.subtle-hover",
          },
          _active: {
            bgColor: "background.functional.error.subtle-active",
          },
          _expanded: {
            bgColor: "background.functional.error.subtle-active",
          },
        },
      },
    },
    {
      loading: false,
      hierarchy: "brand-reversed-bold",
      css: {
        _disabled: {
          bgColor: {
            base: "background.neutral.overlay.subtler",
            _withinWhiteLabelTheme:
              "background.neutral-on-brand.disabled.reversed",
          },
          borderColor: "transparent",
          color: {
            base: "content.neutral.reverse.disable",
            _withinWhiteLabelTheme: "content.neutral.disabled",
          },
        },
        _notDisabled: {
          _hover: {
            bgColor: {
              base: "background.brand.complementary",
              _withinWhiteLabelTheme:
                "background.neutral-on-brand.base.bold-hover",
            },
          },
          _active: {
            bgColor: {
              base: "background.brand.complementary-reversed",
            },
          },
          _expanded: {
            bgColor: {
              base: "background.brand.complementary-reversed",
            },
          },
        },
      },
    },
    {
      loading: false,
      hierarchy: "brand-reversed-subtle",
      css: {
        _disabled: {
          bgColor: {
            base: "background.neutral.overlay.subtler",
            _withinWhiteLabelTheme:
              "background.neutral-on-brand.disabled.reversed",
          },
          borderColor: "transparent",
          color: {
            base: "content.neutral.reverse.disable",
            _withinWhiteLabelTheme: "content.neutral.disabled",
          },
        },
        _notDisabled: {
          _hover: {
            bgColor: {
              base: "background.brand.bolder-hover",
              _withinWhiteLabelTheme:
                "background.neutral-on-brand.base.subtler-hover",
            },
            borderColor: {
              base: "border.brand-complementary-reversed",
              _withinWhiteLabelTheme: "border.neutral-on-brand.bold-hover",
            },
          },
          _active: {
            bgColor: "background.brand.bolder-active",
            borderColor: "border.brand-complementary-reversed",
          },
          _expanded: {
            bgColor: "background.brand.bolder-active",
            borderColor: "border.brand-complementary-reversed",
          },
        },
      },
    },
    {
      loading: false,
      hierarchy: "brand-reversed-subtler",
      css: {
        _disabled: {
          borderColor: "transparent",
          color: {
            base: "content.neutral.reverse.disable",
            _withinWhiteLabelTheme: "content.neutral.disabled",
          },
        },
        _notDisabled: {
          _hover: {
            bgColor: {
              base: "background.brand.bolder-hover",
              _withinWhiteLabelTheme:
                "background.neutral-on-brand.base.subtler-hover",
            },
          },
          _active: {
            bgColor: "background.brand.bolder-active",
          },
          _expanded: {
            bgColor: "background.brand.bolder-active",
          },
        },
      },
    },
    {
      loading: false,
      hierarchy: "reversed-bold",
      css: {
        _disabled: {
          bgColor: "background.neutral.disable.reverse",
          borderColor: "transparent",
          color: "content.neutral.reverse.disable",
        },
        _notDisabled: {
          _hover: {
            bgColor: "background.neutral.base.subtlest-hover",
          },
          _active: {
            bgColor: "background.neutral.base.subtlest-active",
          },
          _expanded: {
            bgColor: "background.neutral.base.subtlest-active",
          },
        },
      },
    },
    {
      loading: false,
      hierarchy: "reversed-subtle",
      css: {
        _disabled: {
          bgColor: "background.neutral.disable.reverse",
          borderColor: "transparent",
          color: "content.neutral.reverse.disable",
        },
        _notDisabled: {
          _hover: {
            bgColor: "background.neutral.reverse.subtlest-hover",
            color: "content.neutral.reverse.bold-hover",
          },
          _active: {
            bgColor: "background.neutral.reverse.subtlest-active",
          },
          _expanded: {
            bgColor: "background.neutral.reverse.subtlest-active",
          },
        },
      },
    },
    {
      loading: false,
      hierarchy: "reversed-subtler",
      css: {
        _disabled: {
          bgColor: "background.neutral.disable.reverse",
          borderColor: "transparent",
          color: "content.neutral.reverse.disable",
        },
        _notDisabled: {
          _hover: {
            bgColor: "background.neutral.reverse.subtlest-hover",
            color: "content.neutral.reverse.bold-hover",
          },
          _active: {
            bgColor: "background.neutral.reverse.subtlest-active",
          },
          _expanded: {
            bgColor: "background.neutral.reverse.subtlest-active",
          },
        },
      },
    },
    // loading
    {
      loading: true,
      hierarchy: "primary-bold",
      css: {
        bgImage:
          "linear-gradient({colors.background.brand.bold-hover}, {colors.background.brand.bold-hover})",
        _withinWhiteLabelTheme: {
          bgImage:
            "linear-gradient({colors.background.neutral-on-brand.base.subtle}, {colors.background.neutral-on-brand.base.subtle})",
        },
      },
    },
    {
      loading: true,
      hierarchy: "primary-subtle",
      css: {
        bgImage:
          "linear-gradient({colors.background.neutral.base.subtlest-hover}, {colors.background.neutral.base.subtlest-hover})",
      },
    },
    {
      loading: true,
      hierarchy: "primary-subtler",
      css: {
        bgImage:
          "linear-gradient({colors.background.neutral.base.subtlest-hover}, {colors.background.neutral.base.subtlest-hover})",
      },
    },
    {
      loading: true,
      hierarchy: "secondary-bold",
      css: {
        bgImage:
          "linear-gradient({colors.background.neutral.base.subtle-hover}, {colors.background.neutral.base.subtle-hover})",
      },
    },
    {
      loading: true,
      hierarchy: "secondary-subtle",
      css: {
        bgImage:
          "linear-gradient({colors.background.neutral.base.subtlest-hover}, {colors.background.neutral.base.subtlest-hover})",
      },
    },
    {
      loading: true,
      hierarchy: "secondary-subtler",
      css: {
        bgImage:
          "linear-gradient({colors.background.neutral.base.subtlest-hover}, {colors.background.neutral.base.subtlest-hover})",
      },
    },
    {
      loading: true,
      hierarchy: "destructive-bold",
      css: {
        bgImage:
          "linear-gradient({colors.background.functional.error.bold-hover}, {colors.background.functional.error.bold-hover})",
      },
    },
    {
      loading: true,
      hierarchy: "destructive-subtle",
      css: {
        bgImage:
          "linear-gradient({colors.background.functional.error.subtle-hover}, {colors.background.functional.error.subtle-hover})",
      },
    },
    {
      loading: true,
      hierarchy: "destructive-subtler",
      css: {
        bgImage:
          "linear-gradient({colors.background.functional.error.subtle-hover}, {colors.background.functional.error.subtle-hover})",
      },
    },
    {
      loading: true,
      hierarchy: "brand-reversed-bold",
      css: {
        bgImage:
          "linear-gradient({colors.background.brand.subtle-hover}, {colors.background.brand.subtle-hover})",
      },
    },
    {
      loading: true,
      hierarchy: "brand-reversed-subtle",
      css: {
        bgImage:
          "linear-gradient({colors.background.brand.bold-hover}, {colors.background.brand.bold-hover})",
        _withinWhiteLabelTheme: {
          bgImage:
            "linear-gradient({colors.background.neutral-on-brand.base.subtle}, {colors.background.neutral-on-brand.base.subtle})",
        },
      },
    },
    {
      loading: true,
      hierarchy: "brand-reversed-subtler",
      css: {
        bgImage:
          "linear-gradient({colors.background.brand.bold-hover}, {colors.background.brand.bold-hover})",
        _withinWhiteLabelTheme: {
          bgImage:
            "linear-gradient({colors.background.neutral-on-brand.base.subtle}, {colors.background.neutral-on-brand.base.subtle})",
        },
      },
    },
    {
      loading: true,
      hierarchy: "reversed-bold",
      css: {
        bgImage:
          "linear-gradient({colors.background.neutral.base.subtlest-hover}, {colors.background.neutral.base.subtlest-hover})",
      },
    },
    {
      loading: true,
      hierarchy: "reversed-subtle",
      css: {
        bgImage:
          "linear-gradient({colors.background.neutral.base.bold-hover}, {colors.background.neutral.base.bold-hover})",
      },
    },
    {
      loading: true,
      hierarchy: "reversed-subtler",
      css: {
        bgImage:
          "linear-gradient({colors.background.neutral.base.bold-hover}, {colors.background.neutral.base.bold-hover})",
      },
    },
    {
      floating: true,
      hierarchy: "ai-bolder",
      css: {
        shadow: "effect.shadow.ai",
      },
    },
    {
      floating: true,
      hierarchy: "ai-bold",
      css: {
        shadow: "effect.shadow.ai",
      },
    },
    {
      floating: true,
      hierarchy: "ai-subtle",
      css: {
        shadow: "effect.shadow.ai",
      },
    },
    {
      floating: true,
      hierarchy: "ai-subtler",
      css: {
        shadow: "effect.shadow.ai",
      },
    },
  ],
});
